import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UserCog, RefreshCw, Activity, Calendar, CheckCircle2, AlertCircle, Bus, Mail } from "lucide-react";
import { PageShell, PageHeader, StatCard, StatGrid, ModernTable, ActionButton } from "@/components/ds";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

function defaultRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 29);
  const iso = (d) => d.toISOString().slice(0, 10);
  return { from: iso(start), to: iso(end) };
}

const pct = (x) => (x == null ? "—" : `${Math.round(x * 100)}%`);

export default function DashboardDrivers() {
  const [range, setRange] = useState(defaultRange);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all"); // all | active | idle

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/dashboards/drivers?from_date=${range.from}&to_date=${range.to}`);
      setData(r.data);
    } catch (e) {
      toast.error("Erreur de chargement du tableau de bord Chauffeurs");
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to]);

  useEffect(() => { load(); }, [load]);

  const totals = data?.totals || {};
  const rows = data?.rows || [];
  const chart = data?.chart_top || [];

  const visibleRows = useMemo(() => {
    if (filter === "active") return rows.filter((r) => r.missions_total > 0);
    if (filter === "idle")   return rows.filter((r) => !r.missions_total);
    return rows;
  }, [rows, filter]);

  return (
    <PageShell testId="dashboard-drivers">
      <PageHeader
        eyebrow="ANALYTICS"
        title="Dashboard Chauffeurs"
        subtitle="Charge de missions, bus principal et ponctualité par chauffeur."
        actions={
          <div className="flex items-center gap-2" data-testid="dashboard-drivers-controls">
            <label className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">Du</label>
            <input type="date" value={range.from}
              onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
              className="border border-zinc-900 px-2 py-1 text-sm font-mono"
              data-testid="dashboard-drivers-from"
            />
            <label className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">au</label>
            <input type="date" value={range.to}
              onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
              className="border border-zinc-900 px-2 py-1 text-sm font-mono"
              data-testid="dashboard-drivers-to"
            />
            <ActionButton onClick={load} icon={RefreshCw} testId="dashboard-drivers-refresh" disabled={loading}>
              Rafraîchir
            </ActionButton>
          </div>
        }
      />

      <StatGrid cols={4} testId="dashboard-drivers-kpis">
        <StatCard icon={UserCog} label="CHAUFFEURS" value={totals.drivers ?? 0} testId="kpi-drv-total" />
        <StatCard icon={Bus} label="AVEC BUS" value={totals.with_vehicle ?? 0}
          hint={`${(totals.drivers || 0) - (totals.with_vehicle || 0)} sans véhicule`}
          tone={(totals.with_vehicle || 0) === (totals.drivers || 0) ? "success" : "warning"}
          testId="kpi-drv-vehicle"
        />
        <StatCard icon={Activity} label="ACTIFS PÉRIODE" value={totals.with_missions ?? 0}
          hint={`${(totals.drivers || 0) - (totals.with_missions || 0)} sans missions`}
          testId="kpi-drv-with-missions"
        />
        <StatCard icon={Calendar} label="MISSIONS" value={totals.missions_total ?? 0}
          accent testId="kpi-drv-missions"
        />
        <StatCard icon={CheckCircle2} label="TERMINÉES" value={totals.missions_completed ?? 0}
          hint={pct(totals.completion_rate)} tone="success" testId="kpi-drv-completed"
        />
        <StatCard icon={AlertCircle} label="EN ATTENTE" value={totals.missions_pending ?? 0} testId="kpi-drv-pending" />
      </StatGrid>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 border border-zinc-900 bg-white p-5" data-testid="dashboard-drivers-chart">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-black uppercase text-lg tracking-tight">Top 10 — Missions par chauffeur</h3>
            <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{range.from} → {range.to}</span>
          </div>
          {chart.length === 0 ? (
            <div className="text-center text-zinc-500 text-xs py-12">Aucune mission sur la période</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chart} margin={{ top: 8, right: 8, left: 0, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={50} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip cursor={{ fill: "#FFF7CC" }} />
                <Bar dataKey="missions" fill="#0ea5e9" stroke="#18181b" strokeWidth={1} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="border border-zinc-900 bg-zinc-900 text-white p-5" data-testid="dashboard-drivers-summary">
          <h3 className="font-display font-black uppercase text-lg tracking-tight mb-4">Couverture</h3>
          <dl className="space-y-3 text-sm">
            <div className="flex items-baseline justify-between border-b border-zinc-700 pb-2">
              <dt className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Total chauffeurs</dt>
              <dd className="font-display font-black text-2xl">{totals.drivers ?? 0}</dd>
            </div>
            <div className="flex items-baseline justify-between border-b border-zinc-700 pb-2">
              <dt className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Avec bus principal</dt>
              <dd className="font-display font-black text-2xl text-emerald-400">{totals.with_vehicle ?? 0}</dd>
            </div>
            <div className="flex items-baseline justify-between border-b border-zinc-700 pb-2">
              <dt className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Inactifs sur période</dt>
              <dd className="font-display font-black text-2xl text-orange-400">
                {(totals.drivers || 0) - (totals.with_missions || 0)}
              </dd>
            </div>
            <div className="pt-1">
              <div className="text-[11px] font-mono uppercase tracking-widest text-[#FFC60A]">Taux complétion global</div>
              <div className="font-display font-black text-4xl text-[#FFC60A]">{pct(totals.completion_rate)}</div>
            </div>
          </dl>
        </div>
      </div>

      <div className="mt-6 flex items-center gap-2 mb-3">
        <h3 className="font-display font-black uppercase text-lg tracking-tight">Détail par chauffeur</h3>
        <div className="ml-auto flex gap-1" data-testid="dashboard-drivers-filter">
          {[
            { k: "all", l: "Tous" },
            { k: "active", l: "Actifs" },
            { k: "idle", l: "Inactifs" },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setFilter(t.k)}
              className={`px-3 py-1 text-[11px] font-mono uppercase tracking-widest border border-zinc-900 transition-colors
                ${filter === t.k ? "bg-zinc-900 text-[#FFC60A]" : "bg-white text-zinc-900 hover:bg-zinc-50"}`}
              data-testid={`dashboard-drivers-filter-${t.k}`}
            >
              {t.l}
            </button>
          ))}
        </div>
      </div>

      <ModernTable
        testId="dashboard-drivers-table"
        stickyHeader
        columns={[
          { key: "name", label: "Chauffeur" },
          { key: "email", label: "Email" },
          { key: "vehicles", label: "Bus principal(s)" },
          { key: "missions_total", label: "Missions", align: "right" },
          { key: "missions_completed", label: "Terminées", align: "right" },
          { key: "missions_pending", label: "En attente", align: "right" },
          { key: "completion_rate", label: "% Compl.", align: "right" },
        ]}
        rows={visibleRows}
        rowKey="id"
        renderCell={(row, col) => {
          switch (col.key) {
            case "name":
              return (
                <div className="flex items-center gap-2" data-testid={`dashboard-drv-row-${row.id}`}>
                  <span className="inline-flex items-center justify-center w-7 h-7 bg-[#FFC60A] text-zinc-900 font-display font-black text-xs">
                    {(row.name || "?").slice(0, 2).toUpperCase()}
                  </span>
                  <span className="font-medium">{row.name || "—"}</span>
                </div>
              );
            case "email":
              return row.email ? (
                <span className="text-xs text-zinc-600 inline-flex items-center gap-1">
                  <Mail size={11} /> {row.email}
                </span>
              ) : <span className="text-zinc-400">—</span>;
            case "vehicles":
              return row.vehicles && row.vehicles.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {row.vehicles.slice(0, 3).map((v) => (
                    <span key={v.id} className="inline-block px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest bg-zinc-100 border border-zinc-300">
                      {v.label}
                    </span>
                  ))}
                  {row.vehicles.length > 3 && (
                    <span className="text-[10px] text-zinc-500">+{row.vehicles.length - 3}</span>
                  )}
                </div>
              ) : <span className="text-zinc-400 text-xs">aucun</span>;
            case "completion_rate": return pct(row.completion_rate);
            default: return row[col.key] ?? <span className="text-zinc-400">—</span>;
          }
        }}
        emptyMessage={loading ? "Chargement…" : "Aucun chauffeur."}
      />
    </PageShell>
  );
}
